import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
    FormControl,
    FormControlStatus,
    FormGroup,
    UntypedFormControl,
    UntypedFormGroup,
    Validators,
} from '@angular/forms';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { EMPTY_STRING, LANGUAGES } from '@modeso/aumico-lib-core-fe';
import { IDynamicKeyValue, INameId } from '@modeso/types__aumico-lib-core-be';
import { IInputOptions } from '@modeso/types__aumico-lib-notes-be';
import { IInputDataType } from '@modeso/types__aumico-lib-notes-be/dist/models/enums/input-type.enum';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { I18NextService } from 'angular-i18next';
import { debounceTime, startWith } from 'rxjs/operators';
import { IInputOptionsFormValue } from '../../../interfaces/input-options-form-value.interface';
import { NotesOptionsBase } from '../../NotesOptionsBase';

@UntilDestroy()
@Component({
    selector: 'aumico-notes-input-options',
    templateUrl: './input-options.component.html',
    styleUrls: ['./input-options.component.scss'],
})
export class InputOptionsFormComponent extends NotesOptionsBase {
    form: UntypedFormGroup | undefined;
    dataTypeSelections: INameId[] = [];
    numberOfDecimalPlacesSelections: INameId[] = [];
    dataType = IInputDataType;
    @Input() options: IInputOptions;
    @Input() languages: string[];
    @Input() language: string = LANGUAGES.SWISS_GERMAN as string;
    @Input() hideReference = false;
    @Output() onFormValueChange = new EventEmitter<IInputOptions>();
    @Output() onFormStatusChange = new EventEmitter<boolean>();

    constructor(
        private matIconRegistry: MatIconRegistry,
        private domSanitizer: DomSanitizer,
        private localizedTexts: I18NextService,
    ) {
        super();
    }

    ngOnInit(): void {
        this.addSvgIcon('interface-delete 1');
        this.prepareDataTypeSelections();
        this.prepareNumberOfDecimalPlacesSelections();

        if (this.options == null) {
            this.initializeEmptyForm();
        } else {
            this.initializeFormWithSavedOptions(this.options);
        }

        this.form.valueChanges.pipe(debounceTime(500), untilDestroyed(this)).subscribe(() => {
            const inputOptionsMappedValue = this.mapInputOptionsFormValueToOutputOptions(this.form.value);
            this.onFormValueChange.emit(inputOptionsMappedValue);
        });

        this.form.statusChanges
            .pipe(startWith(this.form.status), untilDestroyed(this))
            .subscribe((status: FormControlStatus) => {
                if (status === 'VALID') {
                    this.onFormStatusChange.emit(true);
                } else {
                    this.onFormStatusChange.emit(false);
                }
            });
    }

    private initializeEmptyForm(): void {
        this.form = new FormGroup({
            compareYears: new UntypedFormControl(true, Validators.required),
            text: new UntypedFormControl(undefined, Validators.required),
            dataType: new UntypedFormControl(undefined, Validators.required),
            numberOfDecimalPlaces: new UntypedFormControl(undefined),
            useAsParameter: new UntypedFormControl(false),
            parameterName: new UntypedFormControl(undefined),
            title: new FormControl<string>(EMPTY_STRING),
        });
    }

    private initializeFormWithSavedOptions(options: IInputOptions): void {
        const mappedInputOptions = this.mapInputOptionsToFormInputOptions(options);

        this.form = new FormGroup({
            compareYears: new UntypedFormControl(mappedInputOptions.compareYears, Validators.required),
            text: new UntypedFormControl(mappedInputOptions.text, Validators.required),
            dataType: new UntypedFormControl(mappedInputOptions.dataType, Validators.required),
            numberOfDecimalPlaces: new UntypedFormControl(mappedInputOptions.numberOfDecimalPlaces),
            useAsParameter: new UntypedFormControl(mappedInputOptions.useAsParameter),
            parameterName: new UntypedFormControl(mappedInputOptions.parameterName),
            title: new FormControl<string>(mappedInputOptions.title),
        });
    }

    private prepareDataTypeSelections(): void {
        this.dataTypeSelections.push(
            {
                id: IInputDataType.TEXT,
                name: this.localizedTexts.t('aumico_notes_input_options_data_type_text') as string,
            },
            {
                id: IInputDataType.NUMBER,
                name: this.localizedTexts.t('aumico_notes_input_options_data_type_number') as string,
            },
            {
                id: IInputDataType.CURRENCY,
                name: this.localizedTexts.t('aumico_notes_input_options_data_type_currency') as string,
            },
        );
    }

    private prepareNumberOfDecimalPlacesSelections(): void {
        for (let i = 1; i <= 8; i++) {
            this.numberOfDecimalPlacesSelections.push({
                id: i.toString(),
                name: i.toString(),
            });
        }
    }

    private mapInputOptionsToFormInputOptions(inputOptions: IInputOptions): IInputOptionsFormValue {
        return {
            ...inputOptions,
            text: inputOptions.text[this.language],
            dataType: { id: inputOptions.dataType, name: this.getSavedDataTypeLocalizedText(inputOptions.dataType) },
            numberOfDecimalPlaces:
                inputOptions.numberOfDecimalPlaces != null
                    ? {
                          id: inputOptions.numberOfDecimalPlaces.toString(),
                          name: inputOptions.numberOfDecimalPlaces.toString(),
                      }
                    : undefined,
            title: inputOptions.title?.[this.language]?.trim(),
        };
    }

    private getSavedDataTypeLocalizedText(dataType: IInputDataType): string {
        switch (dataType) {
            case IInputDataType.TEXT:
                return this.localizedTexts.t('aumico_notes_input_options_data_type_text') as string;
            case IInputDataType.NUMBER:
                return this.localizedTexts.t('aumico_notes_input_options_data_type_number') as string;
            case IInputDataType.CURRENCY:
                return this.localizedTexts.t('aumico_notes_input_options_data_type_currency') as string;
            default:
                throw new Error('saved data type is not handled');
        }
    }

    private mapInputOptionsFormValueToOutputOptions(formValue: IInputOptionsFormValue): IInputOptions {
        return {
            ...formValue,
            text: this.getTextWithLanguageKeys(formValue),
            dataType: formValue.dataType?.id as IInputDataType,
            numberOfDecimalPlaces: +formValue.numberOfDecimalPlaces?.id,
            title: {
                [this.language]: formValue.title,
            },
        };
    }

    private getTextWithLanguageKeys(formValue: IInputOptionsFormValue): IDynamicKeyValue {
        const textWithLanguageKeys: IDynamicKeyValue = {};
        for (const language of this.languages) {
            textWithLanguageKeys[language] = formValue.text;
        }
        return textWithLanguageKeys;
    }

    private addSvgIcon(name: string): void {
        this.matIconRegistry.addSvgIcon(
            name,
            this.domSanitizer.bypassSecurityTrustResourceUrl(`/assets/icons/${name}.svg`),
        );
    }
}
